import { Transition } from '@headlessui/react';
import { ReactNode, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { Switch } from '../switch/switch';

interface AppAccordionI extends React.HtmlHTMLAttributes<HTMLDivElement> {
    children: ReactNode;
    text: ReactNode;
    buttonClassName?: string;
    defaultOpen?: boolean;
    toggleActive?: boolean;
    toggleIsEnabled?: boolean;
    setToggleIsEnabled?: (value: boolean) => void;
}

export function AppAccordion({
    children,
    text,
    className,
    buttonClassName,
    defaultOpen = true,
    toggleActive = false,
    toggleIsEnabled: externalToggleIsEnabled,
    setToggleIsEnabled,
    ...props
}: AppAccordionI) {
    const [isOpen, setIsOpen] = useState(toggleActive ? !toggleActive : defaultOpen);
    const [isEnabled, setIsEnabled] = useState(externalToggleIsEnabled ?? false);

    useEffect(() => {
        if (externalToggleIsEnabled !== undefined) {
            setIsEnabled(externalToggleIsEnabled);
        }
    }, [externalToggleIsEnabled]);

    function toggleDropdown() {
        if (isEnabled) {
            setIsOpen((prev) => !prev);
        }
    }

    function handleToggleChange(value: boolean) {
        setIsEnabled(value);
        setIsOpen(value);
        setToggleIsEnabled?.(value);
    }

    return (
        <div className="py-4">
            <div
                className={['flex items-center justify-start', className].join(' ')}
                data-testid={`accordion-${text}`}
                {...props}
            >
                {toggleActive && (
                    <Switch
                        id={`toggleDropdown-${text}`}
                        className="mr-4"
                        checked={isEnabled}
                        onChange={(evt) => handleToggleChange(evt.target.checked)}
                    />
                )}
                <button
                    type="button"
                    onClick={toggleDropdown}
                    data-testid={`accordion-${text}-button`}
                    className={twMerge(
                        'text-lg font-bold',
                        toggleActive && !isEnabled && 'text-gray-500',
                        buttonClassName,
                    )}
                    disabled={toggleActive && !isEnabled}
                >
                    <span className="pr-2">{text}</span>
                    <i
                        className={` py-1 ri-arrow-down-s-line transform transition-transform duration-200 ${
                            isOpen ? 'rotate-180' : ''
                        }`}
                    ></i>
                </button>
            </div>
            <Transition
                show={isOpen}
                enter="transition-all transform ease-in-out duration-200"
                enterFrom="opacity-0 -translate-y-2"
                enterTo="opacity-100 translate-y-0"
                leave="transition-all transform ease-in-out duration-200"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 -translate-y-2"
            >
                {children}
            </Transition>
        </div>
    );
}
